import { compact, uniq } from "lodash";
import { useMemo } from "react";

import { FilterOption } from "../types";

/**
 * Generic hook to extract all distinct values from a list of items.
 * An example use case is to display each distinct item in a
 * list of filters.
 * Items are sorted alphabetically by default.
 *
 * @param items - Array of items to extract options from
 * @param extractor - Function to extract a string value from each item
 * @param ignoreSort - Whether to ignore alphabetical sorting
 * @returns Array of FilterOption objects with unique label/value pairs
 */
export const useFilterOptions = <T>(
  items: T[],
  extractor: (item: T) => string | null,
  ignoreSort?: boolean
): FilterOption[] => {
  return useMemo(() => {
    // Extract values, remove nulls, get unique values
    const values = compact(uniq(items.map(extractor)));

    // Sort alphabetically for better UX
    if (!ignoreSort) {
      values.sort((a, b) => a.localeCompare(b));
    }

    return values.map((value) => ({
      label: value,
      value: value,
    }));
  }, [items, extractor, ignoreSort]);
};
